import { AnimatedTr } from './AnimatedTr.component';
import { DryingHerbsFormAction } from './DryingHerbsForm.component';
import { TFunction } from 'i18next';
import { DryingHerbsActionType } from 'pleinchamp-api-client';
import React, { ComponentPropsWithRef, FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { UnderlinedButton } from '@components/buttons/FlatButton/FlatButton.component';
import { Value } from '@form/business/form.types';
import { FormHints, HintProps } from '@form/components/FormHints/FormHints.component';
import { PlcDatePicker } from '@form/components/PlcDatePicker/PlcDatePicker.component';
import { PlcTimePicker } from '@form/components/PlcTimePicker/PlcTimePicker.component';
import { SimpleSelect } from '@form/components/SimpleSelect/SimpleSelect.component';
import { Breakpoint, useIsBreakpointDown } from '@styles/breakpoints';
import { plcDayjs } from '@utils/date';
import { Dayjs } from 'dayjs';

type Props = Omit<ComponentPropsWithRef<typeof AnimatedTr>, 'index' | 'onChange'> &
  HintProps & {
    value: Partial<DryingHerbsFormAction>;
    minDate?: Dayjs;
    maxDate?: Dayjs;
    onChange: (value: Partial<DryingHerbsFormAction>) => void;
    onDelete?: (value: Partial<DryingHerbsFormAction>) => void;
    isSubmitted: boolean;
    actionDisabled?: boolean;
    index?: number | string;
  };

function dryingHerbsActionTypeCodeToValue(code: string | undefined, t: TFunction) {
  if (!code) return undefined;
  return { label: t(`services.dryingHerbs.actions.${code}`), value: code };
}

export const ActionDateTimeRow: FC<Props> = ({
  value,
  minDate,
  maxDate,
  className,
  onChange,
  onDelete,
  isSubmitted,
  actionDisabled,
  isHintVisible,
  validators,
  ...rest
}) => {
  const isMobile = useIsBreakpointDown(Breakpoint.m);
  const { t } = useTranslation(['services']);
  const { type, date, time } = value;

  const dryingHerbsActionOptions: Value[] = useMemo(
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    () => Object.values(DryingHerbsActionType).map(code => dryingHerbsActionTypeCodeToValue(code, t)!),
    []
  );

  return (
    <>
      <AnimatedTr className={className} index={`row-${value.id}`} {...rest}>
        <th>
          <SimpleSelect
            disabled={actionDisabled}
            hideArrow={actionDisabled}
            onSelect={newType =>
              newType ? onChange({ ...value, type: newType.value as DryingHerbsActionType }) : undefined
            }
            options={dryingHerbsActionOptions}
            value={dryingHerbsActionTypeCodeToValue(type, t)}
            variant={isMobile ? 'full-width' : undefined}
          />
        </th>
        <td className="date-wrapper">
          <PlcDatePicker
            isHintVisible={isSubmitted}
            maxDate={maxDate || plcDayjs().add(4, 'day')}
            minDate={minDate}
            onChange={newDate => {
              onChange({ ...value, date: newDate || undefined });
            }}
            value={date}
          />
        </td>
        <td className="time-wrapper">
          <PlcTimePicker
            isHintVisible={true}
            onChange={newTime => onChange({ ...value, time: newTime })}
            value={time}
            variant={isMobile ? undefined : 'small-input'}
          />
        </td>
        <td className="delete-button">
          {!actionDisabled && (
            <UnderlinedButton
              onClick={() => (onDelete ? onDelete(value) : undefined)}
              textProps={{ i18nKey: 'common.delete', variant: 'small' }}
            />
          )}
        </td>
      </AnimatedTr>
      {!validators?.every(v => v.isValid) && (
        <AnimatedTr index={`error-${value.id}`}>
          <td colSpan={4}>
            <FormHints className="plc-mb-m" isHintVisible={true} validators={validators} />
          </td>
        </AnimatedTr>
      )}
    </>
  );
};
